<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">{{ copy.title }}</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="$emit('update:open', false)" />
                </div>
            </template>
            <div class="space-y-4">
                <p>{{ copy.description }}</p>
                <div v-if="sendsSummary" class="flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    <UIcon name="i-ph-whatsapp-logo-duotone" class="mt-0.5 size-4 shrink-0" />
                    <span>The patient will get a hospital admission summary on WhatsApp.</span>
                </div>
                <div v-if="missing.length" class="space-y-2">
                    <p class="text-sm font-medium text-gray-700">Fill in the following first:</p>
                    <ul class="space-y-1">
                        <li v-for="item in missing" :key="item" class="flex items-center gap-2 text-sm text-gray-700">
                            <UIcon name="i-ph-warning-circle-duotone" class="size-4 shrink-0 text-red-400" />
                            <span>{{ item }}</span>
                        </li>
                    </ul>
                </div>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton size="lg" color="white" label="Cancel" @click="$emit('update:open', false)" />
                    <UButton size="lg" :color="copy.color" :label="copy.confirm" :loading="loading" :disabled="missing.length > 0"
                        @click="$emit('confirm')" />
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import { InterventionStatus } from '~/types/fieldPost'

const props = defineProps<{
    open: boolean
    status: InterventionStatus
    missing: string[]
    loading?: boolean
    sendsSummary?: boolean
}>()

defineEmits<{
    'update:open': [boolean]
    'confirm': []
}>()

const COPY = {
    [InterventionStatus.completed]: {
        title: 'Complete Intervention',
        description: 'This marks the intervention as Completed. It can no longer be edited. You can reopen it within 24 hours; after that, only an admin can.',
        confirm: 'Complete',
        color: 'emerald',
    },
    [InterventionStatus.refusedCare]: {
        title: 'Mark as Refused Care',
        description: 'This records that the patient refused care and closes the intervention. You can reopen it within 24 hours; after that, only an admin can.',
        confirm: 'Mark Refused Care',
        color: 'red',
    },
    [InterventionStatus.inProgress]: {
        title: 'Reopen Intervention',
        description: 'This sets the intervention back to In Progress so it can be edited and completed again.',
        confirm: 'Reopen',
        color: 'black',
    },
} as const

const copy = computed(() => COPY[props.status])
</script>
